import { Button } from '@slal/ui/buttons';
import { FormGroup } from '@slal/ui/forms';
import { Col, Container, Row } from '@slal/ui/layout';
import { Modal } from '@slal/ui/modal';
import { FormProvider, yupResolver } from '@slal/ui/react-hook-form/core';
import { SwitchField } from '@slal/ui/react-hook-form/fields';
import { Article, P } from '@slal/ui/typography';
import { useCallback } from 'react';
import { LinkButton, Notice } from '~/components';
import { useApplicationForm } from '~/hooks';
import { communicationPreferencesSchema } from '~/schemas';
import { ApplicationFormKeys, CommunicationPreferencesSchema } from '~/types';
import { StyledCol, StyledLabel, StyledModalBody, StyledModalFooter } from './styles';
import { CommunicationPreferencesModalProps } from './types';

export const CommunicationPreferencesArticle = ({ openModal }: { openModal: () => void }) => {
    return (
        <Article>
            <P>
                Standard Life will send you details about our products, services and offers. You can update{' '}
                <LinkButton type="button" onClick={openModal}>
                    preferences
                </LinkButton>{' '}
                at any time.
            </P>
        </Article>
    );
};

export const CommunicationPreferencesModal = ({
    isOpen,
    closeModal,
    getCommunicationPreferences,
}: CommunicationPreferencesModalProps) => {
    const defaultPreferences = getCommunicationPreferences();

    const { form, handleSubmit } = useApplicationForm<CommunicationPreferencesSchema>(
        {
            defaultValues: {
                [ApplicationFormKeys.COMMUNICATION_PREFERENCES]: defaultPreferences,
            },
            resolver: yupResolver(communicationPreferencesSchema),
        },
        ApplicationFormKeys.COMMUNICATION_PREFERENCES
    );

    const close = useCallback(() => {
        const preferences = getCommunicationPreferences();
        form.reset({
            [ApplicationFormKeys.COMMUNICATION_PREFERENCES]: preferences,
        });
        closeModal();
    }, [closeModal, form, getCommunicationPreferences]);

    return (
        <Modal isOpen={isOpen} onClose={close} title="Offers from Standard Life">
            <FormProvider {...form}>
                <Container>
                    <form onSubmit={handleSubmit(close)}>
                        <StyledModalBody>
                            <Row gutters={false}>
                                <Col col={12} sm={8}>
                                    <Notice>About twice a month</Notice>
                                    <P>
                                        How would you like us to contact you about our products, services and offers to
                                        help you achieve your financial goals?
                                    </P>
                                    <P markdown>
                                        See our [Privacy Policy](https://www.standardlife.co.uk/privacy) for more
                                        details.
                                    </P>
                                </Col>
                                <StyledCol col={12} sm={4}>
                                    <FormGroup inline aria-label="Choose your email preference">
                                        <StyledLabel htmlFor="communicationPreferences.email">Email</StyledLabel>
                                        <SwitchField name="communicationPreferences.email" />
                                    </FormGroup>
                                    <FormGroup inline aria-label="Choose your phone preference">
                                        <StyledLabel htmlFor="communicationPreferences.phone">Phone</StyledLabel>
                                        <SwitchField name="communicationPreferences.phone" />
                                    </FormGroup>
                                    <FormGroup inline aria-label="Choose your text preference">
                                        <StyledLabel htmlFor="communicationPreferences.text">Text</StyledLabel>
                                        <SwitchField name="communicationPreferences.text" />
                                    </FormGroup>
                                </StyledCol>
                            </Row>
                        </StyledModalBody>
                        <StyledModalFooter>
                            <Button type="submit" variant="solid" color="secondary">
                                Save changes
                            </Button>
                        </StyledModalFooter>
                    </form>
                </Container>
            </FormProvider>
        </Modal>
    );
};
